import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { fetchWithHeaders } from '../api/fetchWithHeaders';

interface TenderSupplier {
  tender_supplier_id?: number;
  tender_id?: number;
  nsp_supplier_id?: number;
  supplier_id?: number; // From new API
  supplier_name: string;
  supplier_code?: string;
  is_participant?: boolean;
  is_incumbent?: boolean;
  reason_of_not_participate?: string;
}

interface UseTenderSuppliersOptions {
  negotiation_id?: number;
  tender_id?: number; // Keep for backward compatibility but prefer negotiation_id
}

export const useTenderSuppliers = (options: UseTenderSuppliersOptions = {}) => {
  const [suppliers, setSuppliers] = useState<TenderSupplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSuppliers = async () => {
      // If no negotiation_id is provided, don't fetch
      if (!options.negotiation_id) {
        setSuppliers([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const url = import.meta.env.VITE_API_BASE_URL;
        const token = useAuthStore.getState().token;

        // Use new negotiation-based endpoint
        const response = await fetchWithHeaders(
          `${url}api/v1/tenders/negotiation-packages/${options.negotiation_id}/suppliers`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Map API response to expected format (supplier_id -> nsp_supplier_id)
        const mappedSuppliers: TenderSupplier[] = data.map((s: any) => ({
          ...s,
          nsp_supplier_id: s.supplier_id || s.nsp_supplier_id,
        }));

        setSuppliers(mappedSuppliers);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliers();
  }, [options.negotiation_id]);

  return {
    suppliers,
    loading,
    error,
  };
};